import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import type { RegisterRace } from '@/lib/registerSkillsConfig';
import type { RegisterSkillsSelection } from '@/lib/registerSkillsConfig';

export interface RegisterEventItem {
  id: number;
  name: string;
  description: string;
  isEqualized: boolean;
  running: boolean;
  isRegistered: boolean;
  subWndId: number;
}

export interface RegisterState {
  events: RegisterEventItem[];
  race: RegisterRace;
  refObjId: number;
}

export interface ToggleRegisterResponse {
  success: boolean;
  message?: string;
  error?: string;
  action?: 'register' | 'unregister';
  isRegistered?: boolean;
}

export const REGISTER_QUERY_KEY = ['register'] as const;

export async function fetchRegisterState(): Promise<RegisterState> {
  const { data } = await api.get<{ status: string; data: RegisterState }>('/api/v1/register');
  return data.data;
}

export function useRegister() {
  return useQuery({
    queryKey: REGISTER_QUERY_KEY,
    queryFn: fetchRegisterState,
    staleTime: 30_000,
    refetchInterval: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useRegisterMutations() {
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationFn: async ({
      eventId,
      skills,
      action,
    }: {
      eventId: number;
      action: 'register' | 'unregister';
      skills?: RegisterSkillsSelection | null;
    }) => {
      const { data } = await api.post<ToggleRegisterResponse>('/api/v1/register/event', {
        eventId,
        action,
        skills: skills ?? undefined,
      });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: REGISTER_QUERY_KEY }),
  });

  return { toggle };
}

export function prefetchRegister(queryClient: QueryClient): void {
  void queryClient.prefetchQuery({
    queryKey: REGISTER_QUERY_KEY,
    queryFn: fetchRegisterState,
    staleTime: 30_000,
  });
}
